import { useCallback, useMemo, useState } from 'react'
import {
  Avatar,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  styled,
} from '@mui/material'
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Link as LinkIcon,
  Route as RouteIcon,
} from '@mui/icons-material'
import ReactDatePicker from 'react-datepicker'
import projectDefaultIcon from '../../../../../../../../../../assets/project_icon_default.svg'
import { intl } from '../../../../../../../../../../i18n'
import {
  formatDate,
  formatDateForApi,
} from '../../../../../../../../../../utils/date'
import { UserBasic } from '../../../../../../../../../../domain/value-object/UserBasicVO'
import { Button } from '../../../../../../../../../components/buttons'
import { colorPalette } from '../../../../../../../../../style/colorPalette'
import { MarkdownPreview } from '../../../../../../../../../components/editors/markdown/Markdown/MarkdownPreview'
import '../../../../../../../../../components/editors/markdown/Markdown/styles.scss'
import MultilineTextEditor from '../../../../../../../../../components/editors/multiline-text/MultilineTextEditor'
import Select from '../../../../../../../../../components/editors/select/Select'
import { ProgramIcon } from '../../../../../../../../../components/icons/ProgramIcon'
import { GanttColors, LINK_COLOR_PRESETS } from '../constants'

const DEFAULT_DEPENDENCY_COLOR = GanttColors.DEPENDENCY_LINE
const DEFAULT_CRITICAL_PATH_COLOR = GanttColors.CRITICAL_PATH_LINE

type ScheduledDateRange = {
  startDate?: string
  endDate?: string
}

export type WbsItemLinkSummary = {
  uuid: string
  fromWbsItemUuid: string
  toWbsItemUuid: string
  /** AG Grid 行 uuid（= projectPlanUuid）。行・プロジェクト解決に使う（プログラム管理画面） */
  fromProjectPlanUuid?: string
  toProjectPlanUuid?: string
  fromName: string
  toName: string
  fromIconUrl?: string
  toIconUrl?: string
  fromDate?: string // "YYYY-MM-DD" format
  toDate?: string // "YYYY-MM-DD" format
  fromScheduledDate?: ScheduledDateRange
  toScheduledDate?: ScheduledDateRange
  color: string | null
  description: string | null
  createdAt?: string
  createdBy?: UserBasic
  updatedAt?: string
  updatedBy?: UserBasic
  fromProjectName?: string
  fromProjectIconUrl?: string
  toProjectName?: string
  toProjectIconUrl?: string
  source?: WbsItemLinkSource
  /** 編集可能か。false の場合は編集・削除ボタンを表示しない（プロジェクト側CPなど） */
  editable?: boolean
}

export const WBS_ITEM_LINK_SOURCE = {
  PROGRAM: 'program',
  PROJECT: 'project',
} as const

export type WbsItemLinkSource =
  (typeof WBS_ITEM_LINK_SOURCE)[keyof typeof WBS_ITEM_LINK_SOURCE]

export const isProgramSource = (
  item: Pick<WbsItemLinkSummary, 'source'>
): boolean => item.source === WBS_ITEM_LINK_SOURCE.PROGRAM

export type WbsItemLinkFilterOption = {
  label: string
  value: string
  iconUrl?: string
  // アイコンの描画種別。'program' はプログラムアイコン、'project' はプロジェクトアイコン
  iconType?: 'program' | 'project'
  // プロジェクトのアイコン未設定（デフォルトアイコン表示）かどうか
  isDefaultIcon?: boolean
}

type WbsItemLinkDialogProps = {
  open: boolean
  onClose: () => void
  type: 'dependency' | 'criticalPath'
  items: WbsItemLinkSummary[]
  onUpdateLink?: (
    uuid: string,
    updates: {
      color?: string
      description?: string
      fromDate?: string
      toDate?: string
    }
  ) => void
  onRemoveLink?: (uuid: string) => void
  readOnly?: boolean
  filterOptions?: WbsItemLinkFilterOption[]
  activeFilter?: string
  onFilterChange?: (value: string) => void
}

const WbsItemLinkDialog = ({
  open,
  onClose,
  type,
  items,
  onUpdateLink,
  onRemoveLink,
  readOnly,
  filterOptions,
  activeFilter,
  onFilterChange,
}: WbsItemLinkDialogProps) => {
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [editColor, setEditColor] = useState<string | undefined>()
  const [editDescription, setEditDescription] = useState('')
  const [editFromDate, setEditFromDate] = useState<string | undefined>()
  const [editToDate, setEditToDate] = useState<string | undefined>()
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  const selectedItem = useMemo(
    () => items.find(item => item.uuid === selectedUuid) ?? null,
    [items, selectedUuid]
  )

  const defaultColor =
    type === 'dependency'
      ? DEFAULT_DEPENDENCY_COLOR
      : DEFAULT_CRITICAL_PATH_COLOR

  const titleMessageId =
    type === 'dependency'
      ? 'wbsItemLink.dependency.dialog.title'
      : 'wbsItemLink.criticalPath.dialog.title'

  const typeLabel = intl.formatMessage({ id: titleMessageId })

  const handleSelect = useCallback((uuid: string) => {
    setSelectedUuid(uuid)
    setIsEditing(false)
  }, [])

  const handleStartEdit = useCallback(() => {
    if (!selectedItem) return
    setEditColor(selectedItem.color ?? undefined)
    setEditDescription(selectedItem.description ?? '')
    setEditFromDate(selectedItem.fromDate)
    setEditToDate(selectedItem.toDate)
    setIsEditing(true)
  }, [selectedItem])

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false)
  }, [])

  const handleSaveEdit = useCallback(() => {
    if (!selectedItem) return
    const updates: {
      color?: string
      description?: string
      fromDate?: string
      toDate?: string
    } = {}
    if (editColor !== (selectedItem.color ?? undefined)) {
      updates.color = editColor
    }
    const trimmedDesc = editDescription.trim()
    const originalDesc = (selectedItem.description ?? '').trim()
    if (trimmedDesc !== originalDesc) {
      updates.description = trimmedDesc || undefined
    }
    if (editFromDate !== selectedItem.fromDate) {
      updates.fromDate = editFromDate
    }
    if (editToDate !== selectedItem.toDate) {
      updates.toDate = editToDate
    }

    onUpdateLink?.(selectedItem.uuid, updates)
    setIsEditing(false)
  }, [
    selectedItem,
    editColor,
    editDescription,
    editFromDate,
    editToDate,
    onUpdateLink,
  ])

  const handleRequestDelete = useCallback(() => {
    setDeleteConfirmOpen(true)
  }, [])

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmOpen(false)
  }, [])

  const handleConfirmDelete = useCallback(() => {
    if (!selectedItem) return
    onRemoveLink?.(selectedItem.uuid)
    setDeleteConfirmOpen(false)
    setSelectedUuid(null)
    setIsEditing(false)
  }, [selectedItem, onRemoveLink])

  const handleCloseDialog = useCallback(() => {
    setSelectedUuid(null)
    setIsEditing(false)
    setDeleteConfirmOpen(false)
    onClose()
  }, [onClose])

  const handleDescriptionChange = useCallback((value: string) => {
    setEditDescription(value)
  }, [])

  const hasDateError = useMemo(() => {
    if (type !== 'criticalPath' || !isEditing || !selectedItem) return false
    const isFromValid = isDateInRange(
      editFromDate,
      selectedItem.fromScheduledDate?.startDate,
      selectedItem.fromScheduledDate?.endDate
    )
    const isToValid = isDateInRange(
      editToDate,
      selectedItem.toScheduledDate?.startDate,
      selectedItem.toScheduledDate?.endDate
    )
    return !isFromValid || !isToValid
  }, [type, isEditing, selectedItem, editFromDate, editToDate])

  return (
    <StyledDialog open={open} onClose={handleCloseDialog} maxWidth={false}>
      <StyledDialogTitle>
        <TitleContent>
          {type === 'dependency' ? <StyledLinkIcon /> : <StyledTimelineIcon />}
          <Typography variant="h6" component="span">
            {typeLabel}
          </Typography>
        </TitleContent>
        <IconButton onClick={handleCloseDialog} size="small">
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <StyledDialogContent>
        <ContentWrapper>
          {/* Left Panel - List */}
          <LeftPanelContainer>
            <LeftPanelHeader>
              <Typography variant="subtitle2" fontWeight={600}>
                {intl.formatMessage(
                  { id: 'wbsItemLink.list.title' },
                  { count: items.length }
                )}
              </Typography>
            </LeftPanelHeader>

            {filterOptions && filterOptions.length > 0 && (
              <FilterContainer>
                <Select
                  options={filterOptions}
                  getOptionValue={(opt: WbsItemLinkFilterOption) => opt.value}
                  getOptionLabel={(opt: WbsItemLinkFilterOption) => opt.label}
                  getOptionIcon={(opt: WbsItemLinkFilterOption) => {
                    if (opt.iconType === 'program') {
                      return <ProgramIcon size={20} />
                    }
                    if (opt.iconType === 'project') {
                      return (
                        <FilterProjectIcon
                          isDefault={opt.isDefaultIcon}
                          src={opt.iconUrl || projectDefaultIcon}
                        />
                      )
                    }
                    return null
                  }}
                  value={activeFilter ?? ''}
                  onChange={e => onFilterChange?.(e.target.value as string)}
                  variant="outlined"
                  size="small"
                  fullWidth={true}
                  sx={{
                    '& .MuiSelect-select': {
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '8px 32px 8px 8px',
                    },
                  }}
                />
              </FilterContainer>
            )}

            <StyledList dense={true}>
              {items.length === 0 ? (
                <EmptyListContainer>
                  <Typography color="text.secondary">
                    {intl.formatMessage({ id: 'wbsItemLink.list.empty' })}
                  </Typography>
                </EmptyListContainer>
              ) : (
                items.map(item => (
                  <StyledListItem
                    key={item.uuid}
                    isSelected={selectedUuid === item.uuid}
                    onClick={() => handleSelect(item.uuid)}
                  >
                    {type === 'criticalPath' && (
                      <ColorDot dotColor={item.color ?? defaultColor} />
                    )}
                    <ListItemContent>
                      {item.source && (
                        <SourceChip
                          label={intl.formatMessage({
                            id: isProgramSource(item)
                              ? 'wbsItemLink.source.program'
                              : 'wbsItemLink.source.project',
                          })}
                          size="small"
                          variant="outlined"
                          isProgram={isProgramSource(item)}
                        />
                      )}
                      <WbsItemRow>
                        <RowLabel variant="caption" color="text.secondary">
                          From:
                        </RowLabel>
                        <ItemLines>
                          {item.fromProjectName && (
                            <LineRow>
                              <ProjectIconSmall
                                isDefault={!item.fromProjectIconUrl}
                                src={
                                  item.fromProjectIconUrl || projectDefaultIcon
                                }
                              />
                              <ProjectNameText>
                                {item.fromProjectName}
                              </ProjectNameText>
                            </LineRow>
                          )}
                          <LineRow>
                            {item.fromIconUrl && (
                              <WbsItemIcon src={item.fromIconUrl} />
                            )}
                            <Typography variant="body2" noWrap={true}>
                              {item.fromName}
                            </Typography>
                            {type === 'criticalPath' && item.fromDate && (
                              <ListDateText>
                                {formatDate(item.fromDate)}
                              </ListDateText>
                            )}
                          </LineRow>
                        </ItemLines>
                      </WbsItemRow>
                      <WbsItemRow>
                        <RowLabel variant="caption" color="text.secondary">
                          To:
                        </RowLabel>
                        <ItemLines>
                          {item.toProjectName && (
                            <LineRow>
                              <ProjectIconSmall
                                isDefault={!item.toProjectIconUrl}
                                src={
                                  item.toProjectIconUrl || projectDefaultIcon
                                }
                              />
                              <ProjectNameText>
                                {item.toProjectName}
                              </ProjectNameText>
                            </LineRow>
                          )}
                          <LineRow>
                            {item.toIconUrl && (
                              <WbsItemIcon src={item.toIconUrl} />
                            )}
                            <Typography variant="body2" noWrap={true}>
                              {item.toName}
                            </Typography>
                            {type === 'criticalPath' && item.toDate && (
                              <ListDateText>
                                {formatDate(item.toDate)}
                              </ListDateText>
                            )}
                          </LineRow>
                        </ItemLines>
                      </WbsItemRow>
                    </ListItemContent>
                  </StyledListItem>
                ))
              )}
            </StyledList>
          </LeftPanelContainer>

          {/* Right Panel - Detail */}
          <RightPanelWrapper>
            {selectedItem && !isEditing && (
              <ViewPanel
                type={type}
                item={selectedItem}
                defaultColor={defaultColor}
                onEdit={handleStartEdit}
                onDelete={handleRequestDelete}
                readOnly={readOnly}
              />
            )}

            {selectedItem && isEditing && (
              <>
                <EditPanel
                  type={type}
                  item={selectedItem}
                  editColor={editColor}
                  editDescription={editDescription}
                  editFromDate={editFromDate}
                  editToDate={editToDate}
                  onColorChange={setEditColor}
                  onDescriptionChange={handleDescriptionChange}
                  onFromDateChange={setEditFromDate}
                  onToDateChange={setEditToDate}
                />
                <RightPanelFooter>
                  <Button
                    colorPattern="monotone"
                    variant="outlined"
                    onClick={handleCancelEdit}
                  >
                    {intl.formatMessage({ id: 'wbsItemLink.button.cancel' })}
                  </Button>
                  <Button
                    colorPattern="skyBlue"
                    onClick={handleSaveEdit}
                    disabled={hasDateError}
                  >
                    {intl.formatMessage({ id: 'wbsItemLink.button.save' })}
                  </Button>
                </RightPanelFooter>
              </>
            )}

            {!selectedItem && (
              <EmptyStateContainer>
                <Typography fontWeight={600}>
                  {intl.formatMessage({ id: 'wbsItemLink.detail.empty' })}
                </Typography>
              </EmptyStateContainer>
            )}
          </RightPanelWrapper>
        </ContentWrapper>
      </StyledDialogContent>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        maxWidth="xs"
      >
        <DialogTitle>
          {intl.formatMessage({ id: 'wbsItemLink.delete.confirm.title' })}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {intl.formatMessage(
              { id: 'wbsItemLink.delete.confirm.message' },
              { type: typeLabel }
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            colorPattern="monotone"
            variant="outlined"
            onClick={handleCancelDelete}
          >
            {intl.formatMessage({ id: 'wbsItemLink.button.cancel' })}
          </Button>
          <Button colorPattern="pink" onClick={handleConfirmDelete}>
            {intl.formatMessage({ id: 'wbsItemLink.button.delete' })}
          </Button>
        </DialogActions>
      </Dialog>
    </StyledDialog>
  )
}

// ============================================================================
// Sub-components
// ============================================================================

const ViewPanel = ({
  type,
  item,
  defaultColor,
  onEdit,
  onDelete,
  readOnly,
}: {
  type: 'dependency' | 'criticalPath'
  item: WbsItemLinkSummary
  defaultColor: string
  onEdit: () => void
  onDelete: () => void
  readOnly?: boolean
}) => (
  <>
    <PanelHeader>
      <PanelHeaderContent>
        <Typography variant="subtitle2" fontWeight={600}>
          {intl.formatMessage({ id: 'wbsItemLink.detail.basicInfo' })}
        </Typography>
        {!readOnly && item.editable !== false && (
          <ActionButtonsContainer>
            {type === 'criticalPath' && (
              <Button
                colorPattern="skyBlue"
                variant="outlined"
                onClick={onEdit}
              >
                <SmallEditIcon />
                {intl.formatMessage({ id: 'wbsItemLink.button.edit' })}
              </Button>
            )}
            <Button
              colorPattern="monotone"
              variant="outlined"
              onClick={onDelete}
            >
              <SmallDeleteIcon />
              {intl.formatMessage({ id: 'wbsItemLink.button.delete' })}
            </Button>
          </ActionButtonsContainer>
        )}
      </PanelHeaderContent>
    </PanelHeader>

    <PanelContent>
      <FieldsContainer>
        <div>
          <Typography variant="caption" color="text.secondary" display="block">
            {intl.formatMessage({ id: 'wbsItemLink.detail.from' })}
          </Typography>
          <DetailWbsItemRow>
            {item.fromProjectName && (
              <LineRow>
                <ProjectIconSmall
                  isDefault={!item.fromProjectIconUrl}
                  src={item.fromProjectIconUrl || projectDefaultIcon}
                />
                <ProjectNameText>{item.fromProjectName}</ProjectNameText>
              </LineRow>
            )}
            <LineRow>
              {item.fromIconUrl && <WbsItemIcon src={item.fromIconUrl} />}
              <Typography variant="body2">{item.fromName}</Typography>
            </LineRow>
          </DetailWbsItemRow>
          {type === 'criticalPath' && item.fromDate && (
            <DetailDateText>{formatDate(item.fromDate)}</DetailDateText>
          )}
        </div>

        <div>
          <Typography variant="caption" color="text.secondary" display="block">
            {intl.formatMessage({ id: 'wbsItemLink.detail.to' })}
          </Typography>
          <DetailWbsItemRow>
            {item.toProjectName && (
              <LineRow>
                <ProjectIconSmall
                  isDefault={!item.toProjectIconUrl}
                  src={item.toProjectIconUrl || projectDefaultIcon}
                />
                <ProjectNameText>{item.toProjectName}</ProjectNameText>
              </LineRow>
            )}
            <LineRow>
              {item.toIconUrl && <WbsItemIcon src={item.toIconUrl} />}
              <Typography variant="body2">{item.toName}</Typography>
            </LineRow>
          </DetailWbsItemRow>
          {type === 'criticalPath' && item.toDate && (
            <DetailDateText>{formatDate(item.toDate)}</DetailDateText>
          )}
        </div>

        {type === 'criticalPath' && (
          <div>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {intl.formatMessage({ id: 'wbsItemLink.detail.color' })}
            </Typography>
            <ColorDot dotColor={item.color ?? defaultColor} />
          </div>
        )}

        {type === 'criticalPath' && (
          <div>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {intl.formatMessage({ id: 'wbsItemLink.detail.description' })}
            </Typography>
            {item.description && (
              <MarkdownContainer className="markdown-body">
                <MarkdownPreview text={item.description} />
              </MarkdownContainer>
            )}
          </div>
        )}

        <MetaInfoBox>
          <MetaInfoContent>
            <Typography variant="caption" color="text.secondary">
              {intl.formatMessage({ id: 'wbsItemLink.detail.created' })}:
            </Typography>
            {item.createdBy?.iconUrl && (
              <SmallAvatar src={item.createdBy.iconUrl} />
            )}
            <Typography variant="caption" color="text.secondary">
              {item.createdBy?.name} ({item.createdAt})
            </Typography>
          </MetaInfoContent>
          {item.updatedAt && (
            <MetaInfoContent>
              <Typography variant="caption" color="text.secondary">
                {intl.formatMessage({ id: 'wbsItemLink.detail.updated' })}:
              </Typography>
              {item.updatedBy?.iconUrl && (
                <SmallAvatar src={item.updatedBy.iconUrl} />
              )}
              <Typography variant="caption" color="text.secondary">
                {item.updatedBy?.name} ({item.updatedAt})
              </Typography>
            </MetaInfoContent>
          )}
        </MetaInfoBox>
      </FieldsContainer>
    </PanelContent>
  </>
)

const parseDateString = (dateStr: string | undefined): Date | undefined => {
  if (!dateStr) return undefined
  const d = new Date(dateStr)
  return isNaN(d.getTime()) ? undefined : d
}

const isDateInRange = (
  dateStr: string | undefined,
  minDateStr: string | undefined,
  maxDateStr: string | undefined
): boolean => {
  if (!dateStr) return false
  if (minDateStr && dateStr < minDateStr) return false
  if (maxDateStr && dateStr > maxDateStr) return false
  return true
}

const EditPanel = ({
  type,
  item,
  editColor,
  editDescription,
  editFromDate,
  editToDate,
  onColorChange,
  onDescriptionChange,
  onFromDateChange,
  onToDateChange,
}: {
  type: 'dependency' | 'criticalPath'
  item: WbsItemLinkSummary
  editColor: string | undefined
  editDescription: string
  editFromDate: string | undefined
  editToDate: string | undefined
  onColorChange: (color: string) => void
  onDescriptionChange: (value: string) => void
  onFromDateChange: (value: string | undefined) => void
  onToDateChange: (value: string | undefined) => void
}) => {
  const handleFromDateChange = useCallback(
    (date: Date | null) => {
      onFromDateChange(date ? formatDateForApi(date) : undefined)
    },
    [onFromDateChange]
  )

  const handleToDateChange = useCallback(
    (date: Date | null) => {
      onToDateChange(date ? formatDateForApi(date) : undefined)
    },
    [onToDateChange]
  )

  const isFromDateError =
    type === 'criticalPath' &&
    !isDateInRange(
      editFromDate,
      item.fromScheduledDate?.startDate,
      item.fromScheduledDate?.endDate
    )

  const isToDateError =
    type === 'criticalPath' &&
    !isDateInRange(
      editToDate,
      item.toScheduledDate?.startDate,
      item.toScheduledDate?.endDate
    )

  return (
    <>
      <PanelHeader>
        <Typography variant="subtitle2" fontWeight={600}>
          {intl.formatMessage({ id: 'wbsItemLink.button.edit' })}
        </Typography>
      </PanelHeader>

      <PanelContent>
        <FieldsContainer>
          <div>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {intl.formatMessage({ id: 'wbsItemLink.detail.from' })}
            </Typography>
            <DetailWbsItemRow>
              {item.fromProjectName && (
                <LineRow>
                  <ProjectIconSmall
                    isDefault={!item.fromProjectIconUrl}
                    src={item.fromProjectIconUrl || projectDefaultIcon}
                  />
                  <ProjectNameText>{item.fromProjectName}</ProjectNameText>
                </LineRow>
              )}
              <LineRow>
                {item.fromIconUrl && <WbsItemIcon src={item.fromIconUrl} />}
                <Typography variant="body2">{item.fromName}</Typography>
              </LineRow>
            </DetailWbsItemRow>
            {type === 'criticalPath' && (
              <>
                <DatePickerWrapper hasError={isFromDateError}>
                  <ReactDatePicker
                    selected={parseDateString(editFromDate)}
                    onChange={handleFromDateChange}
                    dateFormat="yyyy/MM/dd"
                    placeholderText="YYYY/MM/DD"
                    minDate={parseDateString(item.fromScheduledDate?.startDate)}
                    maxDate={parseDateString(item.fromScheduledDate?.endDate)}
                  />
                </DatePickerWrapper>
                {isFromDateError && (
                  <DateErrorText>
                    {intl.formatMessage({
                      id: 'wbsItemLink.validation.dateOutOfRange',
                    })}
                  </DateErrorText>
                )}
              </>
            )}
          </div>

          <div>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {intl.formatMessage({ id: 'wbsItemLink.detail.to' })}
            </Typography>
            <DetailWbsItemRow>
              {item.toProjectName && (
                <LineRow>
                  <ProjectIconSmall
                    isDefault={!item.toProjectIconUrl}
                    src={item.toProjectIconUrl || projectDefaultIcon}
                  />
                  <ProjectNameText>{item.toProjectName}</ProjectNameText>
                </LineRow>
              )}
              <LineRow>
                {item.toIconUrl && <WbsItemIcon src={item.toIconUrl} />}
                <Typography variant="body2">{item.toName}</Typography>
              </LineRow>
            </DetailWbsItemRow>
            {type === 'criticalPath' && (
              <>
                <DatePickerWrapper hasError={isToDateError}>
                  <ReactDatePicker
                    selected={parseDateString(editToDate)}
                    onChange={handleToDateChange}
                    dateFormat="yyyy/MM/dd"
                    placeholderText="YYYY/MM/DD"
                    minDate={parseDateString(item.toScheduledDate?.startDate)}
                    maxDate={parseDateString(item.toScheduledDate?.endDate)}
                  />
                </DatePickerWrapper>
                {isToDateError && (
                  <DateErrorText>
                    {intl.formatMessage({
                      id: 'wbsItemLink.validation.dateOutOfRange',
                    })}
                  </DateErrorText>
                )}
              </>
            )}
          </div>

          <div>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {intl.formatMessage({ id: 'wbsItemLink.detail.color' })}
            </Typography>
            <ToggleButtonGroup
              exclusive={true}
              size="small"
              value={editColor ?? null}
              onChange={(_, v) => {
                if (v) onColorChange(v)
              }}
            >
              {LINK_COLOR_PRESETS.map(preset => (
                <ToggleButton key={preset} value={preset}>
                  <ColorCircle bgcolor={preset} />
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </div>

          <div>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {intl.formatMessage({ id: 'wbsItemLink.detail.description' })}
            </Typography>
            <DescriptionContainer>
              <MultilineTextEditor
                value={editDescription}
                setValue={onDescriptionChange}
                disableSbsMode={true}
              />
            </DescriptionContainer>
          </div>
        </FieldsContainer>
      </PanelContent>
    </>
  )
}

// ============================================================================
// Styled Components
// ============================================================================

// Dialog
const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    width: 1000,
    height: 770,
  },
})

const StyledDialogTitle = styled(DialogTitle)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
  paddingBottom: 16,
})

const TitleContent = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
})

const StyledLinkIcon = styled(LinkIcon)({
  color: colorPalette.monotone[5],
})

const StyledTimelineIcon = styled(RouteIcon)({
  color: colorPalette.pink[5],
})

const StyledDialogContent = styled(DialogContent)({
  padding: 0,
  flex: 1,
  overflow: 'hidden',
})

const ContentWrapper = styled('div')({
  display: 'flex',
  height: '100%',
})

// Left Panel
const LeftPanelContainer = styled('div')({
  width: 380,
  borderRight: `1px solid ${colorPalette.monotone[2]}`,
  display: 'flex',
  flexDirection: 'column',
})

const LeftPanelHeader = styled('div')({
  padding: 16,
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
})

const StyledList = styled(List)({
  flex: 1,
  overflow: 'auto',
  paddingTop: 0,
  paddingBottom: 0,
})

const EmptyListContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  paddingTop: 32,
  paddingBottom: 32,
})

const StyledListItem = styled(ListItem, {
  shouldForwardProp: prop => prop !== 'isSelected',
})<{ isSelected: boolean }>(({ isSelected }) => ({
  borderBottom: `1px solid ${colorPalette.monotone[1]}`,
  cursor: 'pointer',
  gap: 8,
  padding: '8px 16px',
  backgroundColor: isSelected ? colorPalette.skyBlue[1] : 'transparent',
  '&:hover': {
    backgroundColor: isSelected
      ? colorPalette.skyBlue[1]
      : colorPalette.monotone[1],
  },
}))

const ColorDot = styled('div', {
  shouldForwardProp: prop => prop !== 'dotColor',
})<{ dotColor: string }>(({ dotColor }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: dotColor,
  flexShrink: 0,
}))

const ListItemContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  overflow: 'hidden',
  flex: 1,
})

const WbsItemRow = styled('div')({
  display: 'flex',
  // プロジェクト名と工程名を縦に並べるため、ラベル（From/To）は上揃えにする
  alignItems: 'flex-start',
  gap: 4,
})

// プロジェクト名の行・工程名の行を縦に積むコンテナ
const ItemLines = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
  flexGrow: 1,
})

// アイコン＋名称を横並びにする1行
const LineRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  minWidth: 0,
})

const RowLabel = styled(Typography)({
  width: 30,
  flexShrink: 0,
})

const WbsItemIcon = styled('img')({
  width: 16,
  height: 16,
  flexShrink: 0,
})

const ListDateText = styled('span')({
  color: colorPalette.monotone[4],
  fontSize: 12,
  flexShrink: 0,
  marginLeft: 'auto',
})

const DetailWbsItemRow = styled('div')({
  display: 'flex',
  // プロジェクト名と工程名を縦に並べる
  flexDirection: 'column',
  gap: 2,
  marginTop: 2,
  minWidth: 0,
})

const DetailDateText = styled(Typography)({
  color: colorPalette.monotone[4],
  fontSize: 13,
  marginTop: 2,
})

// Right Panel
const RightPanelWrapper = styled('div')({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
})

const EmptyStateContainer = styled('div')({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  color: colorPalette.monotone[4],
  gap: 8,
})

const PanelHeader = styled('div')({
  padding: 16,
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
})

const PanelHeaderContent = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
})

const ActionButtonsContainer = styled('div')({
  display: 'flex',
  gap: 8,
})

const PanelContent = styled('div')({
  padding: 16,
  flex: 1,
  overflow: 'auto',
})

const FieldsContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
})

const RightPanelFooter = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
  padding: 16,
  borderTop: `1px solid ${colorPalette.monotone[2]}`,
})

const SmallEditIcon = styled(EditIcon)({
  fontSize: 16,
})

const SmallDeleteIcon = styled(DeleteIcon)({
  fontSize: 16,
})

const ColorCircle = styled('div', {
  shouldForwardProp: prop => prop !== 'bgcolor',
})<{ bgcolor: string }>(({ bgcolor }) => ({
  width: 20,
  height: 20,
  borderRadius: '50%',
  backgroundColor: bgcolor,
}))

const DatePickerWrapper = styled('div', {
  shouldForwardProp: prop => prop !== 'hasError',
})<{ hasError?: boolean }>(({ hasError }) => ({
  marginTop: 4,
  '& .react-datepicker-wrapper': {
    width: '100%',
  },
  '& input': {
    width: '100%',
    padding: '6px 8px',
    border: `1px solid ${hasError ? colorPalette.pink[5] : colorPalette.monotone[2]}`,
    borderRadius: 4,
    fontSize: 14,
    outline: 'none',
    '&:focus': {
      borderColor: hasError ? colorPalette.pink[5] : colorPalette.skyBlue[5],
    },
  },
}))

const DateErrorText = styled('span')({
  color: colorPalette.pink[5],
  fontSize: 12,
  marginTop: 2,
  display: 'block',
})

const DescriptionContainer = styled('div')({
  height: 250,
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: 4,
  overflow: 'hidden',
})

const MarkdownContainer = styled('div')({
  fontSize: 14,
})

const MetaInfoBox = styled('div')({
  backgroundColor: colorPalette.monotone[1],
  padding: 12,
  borderRadius: 4,
  marginTop: 8,
})

const MetaInfoContent = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
})

const SmallAvatar = styled(Avatar)({
  width: 16,
  height: 16,
})

// Filter
const FilterContainer = styled('div')({
  padding: '8px 16px',
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
  flexShrink: 0,
})

// Source Chip
const SourceChip = styled(Chip, {
  shouldForwardProp: prop => prop !== 'isProgram',
})<{ isProgram: boolean }>(({ isProgram }) => ({
  alignSelf: 'flex-start',
  marginBottom: 2,
  height: 20,
  fontSize: 11,
  borderColor: isProgram ? colorPalette.skyBlue[5] : colorPalette.monotone[3],
  color: isProgram ? colorPalette.skyBlue[7] : colorPalette.monotone[5],
}))

// Project Info
const ProjectIconSmall = styled('img', {
  shouldForwardProp: prop => prop !== 'isDefault',
})<{ isDefault?: boolean }>(({ isDefault }) => ({
  width: 14,
  height: 14,
  flexShrink: 0,
  // デフォルトアイコンは角丸正方形のSVGなので、円形に切り抜かず正方形のまま表示する
  ...(isDefault ? {} : { borderRadius: '50%', objectFit: 'cover' }),
}))

const ProjectNameText = styled('span')({
  fontSize: 11,
  color: colorPalette.monotone[4],
  whiteSpace: 'nowrap',
})

// フィルタのドロップダウンに表示するプロジェクトアイコン。
// デフォルトアイコンは角丸正方形のSVGなので円形に切り抜かず正方形のまま表示する。
const FilterProjectIcon = styled('img', {
  shouldForwardProp: prop => prop !== 'isDefault',
})<{ isDefault?: boolean }>(({ isDefault }) => ({
  width: 20,
  height: 20,
  flexShrink: 0,
  ...(isDefault ? {} : { borderRadius: '50%', objectFit: 'cover' }),
}))

export { WbsItemLinkDialog }
